import { type JSX,SyntheticEvent, useEffect, useState } from 'react';
import { Outlet, useLocation,useParams } from 'react-router-dom';

import { CircularProgress, Tabs,Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { NotFound } from 'common/components/NotFound';
import { Page } from 'common/components/Page';
import { selectPageInfo, setPageInfo } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { ResourceDetailHeader } from 'common/components/ResourceDetailHeader';
import { StyledBreadcrumbs } from 'common/components/StyledBreadcrumbs';
import { StyledTab } from 'common/components/StyledTab';
import { Routes, RoutesNameMap } from 'common/constants/routes';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchRoleDetails, FetchRoleDetailsResponse } from 'common/services/api/Roles/Roles.thunk';
import { getLastPathSegment } from 'common/utils/locationHelpers';

export function RoleDetails(): JSX.Element {
    const [currentTab, setCurrentTab] = useState<Routes>(Routes.Users);
    const [processing, setProcessing] = useState<boolean>(true);
    const location = useLocation();

    const [error, setError] = useState<string>('');
    const params = useParams();
    useEffect(() => {
        const lastPathSegment = getLastPathSegment();
        setCurrentTab(lastPathSegment as Routes);
    }, [location]);

    const dispatch = useAppDispatch();
    useEffect(() => {
        const fetchParams = {
            id: params?.roleId ?? '',
            tenantId: params?.tenantId ?? '',
            applicationSuiteId: params?.suiteId ?? ''
        };
        dispatch(fetchRoleDetails(fetchParams))
            .unwrap()
            .then((result) => {
                dispatch(setPageInfo(result));
            })
            .catch((err) => {
                setError(err.message);
            })
            .finally(() => {
                setProcessing(false);
            });
    }, []);

    const getTabLink = (key: string) => (
        `/${Routes.ApplicationSuites}/${params.suiteId}/${Routes.Tenants}/${params.tenantId}/roles/${params.roleId}/${key}`
    );

    const handleChangeTab = (_event: SyntheticEvent, newValue: Routes) => {
        setCurrentTab(newValue);
    };

    const roleDetails = useAppSelector(selectPageInfo) as FetchRoleDetailsResponse;

    const errorCode = extractStatusCode(error);

    if (errorCode === 404) {
        return <NotFound />;
    }

    if (errorCode && errorCode !== 200) {
        return <p>Something went wrong</p>;
    }

    if(processing) {
        return <CircularProgress color='primary'/>;
    }

    return (
        <Page
            title={`${roleDetails?.name} - ${RoutesNameMap.get(Routes.Role)}`}
            header={(
                <Typography variant="h5" gutterBottom component="div">
                    {roleDetails?.id &&
                        <StyledBreadcrumbs
                            guidNameMappings={[{
                                resourceId: params.suiteId ?? '',
                                resourceName: roleDetails?.applicationSuiteName || ''
                            },{
                                resourceId: params.tenantId ?? '',
                                resourceName: roleDetails?.tenantName || ''
                            },{
                                resourceId: params.roleId ?? '',
                                resourceName: roleDetails?.name || ''
                            }]}
                        /> }
                    {roleDetails?.id &&
                        <ResourceDetailHeader
                            resourceType='Role'
                            resourceName={ roleDetails?.name || '' }
                            resourceId={ params.roleId ?? ''}
                        />}
                </Typography>
            )}
        >
            <Tabs
                sx={{ mb: '-60px', zIndex:1200, width: '40rem'  }}
                onChange={handleChangeTab}
                value={currentTab}
            >
                <StyledTab
                    disabled={false}
                    label={RoutesNameMap.get(Routes.Users)}
                    key={Routes.Users}
                    link={getTabLink(Routes.Users)}
                    value={Routes.Users}
                />
                <StyledTab
                    disabled={false}
                    label={RoutesNameMap.get(Routes.UserGroups)}
                    key={Routes.UserGroups}
                    link={getTabLink(Routes.UserGroups)}
                    value={Routes.UserGroups}
                />
                <StyledTab
                    disabled={false}
                    label={RoutesNameMap.get(Routes.Claims)}
                    key={Routes.Claims}
                    link={getTabLink(Routes.Claims)}
                    value={Routes.Claims}
                />
            </Tabs>
            <Outlet />
        </Page>
    );
}
